import React, { useContext, forwardRef } from 'react';
import { __rest } from 'tslib';
import { v4 } from 'uuid';
import ReactDOM from 'react-dom';
import { TransitionGroup, Transition } from 'react-transition-group';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Slot } from '@radix-ui/react-slot';
import clsx from 'clsx';

var DEFAULT_ANIMATION_DURATION = 80;
var DEFAULT_PLACEMENT = 'top';

var createContext = function createContext(component) {
  return /*#__PURE__*/React.createContext({
    Component: component,
    placement: DEFAULT_PLACEMENT,
    animationDuration: DEFAULT_ANIMATION_DURATION,
    q: [],
    setQ: function setQ() {},
    refs: {},
    addToast: function addToast() {
      return 'noop';
    },
    updateToast: function updateToast() {},
    removeToast: function removeToast() {},
    removeAllToasts: function removeAllToasts() {},
    removeAllToastsByName: function removeAllToastsByName() {},
    registerRef: function registerRef() {},
    removeRef: function removeRef() {}
  });
};

var useWindowSize = function useWindowSize() {
  var _React$useState = React.useState([0, 0]),
      size = _React$useState[0],
      setSize = _React$useState[1];

  React.useLayoutEffect(function () {
    var updateSize = function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    };

    window.addEventListener('resize', updateSize);
    updateSize();
    return function () {
      return window.removeEventListener('resize', updateSize);
    };
  }, []);
  return size;
};

var createViewport = function createViewport(context) {
  return function (_ref) {
    var portalInto = _ref.portalInto;

    var renderToast = function renderToast(toast) {
      var removeMe = function removeMe() {
        setToasts(function (t) {
          return t.filter(function (tst) {
            return tst.id !== toast.id;
          });
        });
      };

      if (!toast.parentRef.current) return null;
      var styles = {};
      var disableTransitions = false;

      if (toast.toastRef.current) {
        if (toast.changedSizeRecently) {
          disableTransitions = true;
          toast.changedSizeRecently = false;
        }

        var MARGIN = 4;
        var toastRect = toast.toastRef.current.getBoundingClientRect();
        var parentRect = toast.parentRef.current.getBoundingClientRect();
        var parentX = parentRect.left + window.pageXOffset;
        var parentY = parentRect.top + window.pageYOffset;
        var parentEndX = parentRect.right + window.pageXOffset;
        var parentEndY = parentRect.bottom + window.pageYOffset;
        var neighbourToasts = toasts.filter(function (t) {
          return t.parentName === toast.parentName && t.placement === toast.placement && t.id !== toast.id && t.createdAt < toast.createdAt;
        });
        var neighbourToastsHeight = neighbourToasts.reduce(function (height, toastDetails) {
          return height + toastDetails.cachedSize[1] + MARGIN;
        }, 0);
        styles = {
          position: 'absolute'
        };

        if (toast.placement === 'top') {
          styles.top = parentY - toastRect.height - MARGIN - neighbourToastsHeight;
          styles.left = (parentX + parentEndX) / 2 - toastRect.width / 2;
        }

        if (toast.placement === 'bottom') {
          styles.top = parentEndY + MARGIN + neighbourToastsHeight;
          styles.left = (parentX + parentEndX) / 2 - toastRect.width / 2;
        }

        if (toast.placement === 'left') {
          styles.top = (parentY + parentEndY) / 2 - toastRect.height / 2 - neighbourToastsHeight;
          styles.left = parentX - toastRect.width - MARGIN;
        }

        if (toast.placement === 'right') {
          styles.top = (parentY + parentEndY) / 2 - toastRect.height / 2 - neighbourToastsHeight;
          styles.left = parentEndX + MARGIN;
        }

        if (styles.left && styles.left < 0) styles.left = MARGIN;
        if (styles.left && styles.left + toastRect.width > document.body.scrollWidth) styles.left = document.body.scrollWidth - toastRect.width - MARGIN;
      } else {
        disableTransitions = true;
        styles = {
          position: 'absolute',
          left: '-1000px'
        };
      }

      return /*#__PURE__*/React.createElement(Transition, {
        unmountOnExit: true,
        timeout: animationDuration,
        key: toast.id
      }, function (state) {
        return /*#__PURE__*/React.createElement(Component, {
          placement: toast.placement,
          animation: {
            state: state,
            duration: animationDuration,
            disableTransitions: disableTransitions
          },
          style: styles,
          id: toast.id,
          removeMe: removeMe,
          name: toast.parentName,
          data: toast.data,
          ref: toast.attachRef
        });
      });
    };

    var _useWindowSize = useWindowSize();
        _useWindowSize[0];
        _useWindowSize[1];

    var _React$useContext = React.useContext(context),
        q = _React$useContext.q,
        setQ = _React$useContext.setQ,
        Component = _React$useContext.Component,
        animationDuration = _React$useContext.animationDuration;

    var _React$useState2 = React.useState([]),
        toasts = _React$useState2[0],
        setToasts = _React$useState2[1];

    React.useEffect(function () {
      q.forEach(function (action) {
        if (action.type === 'create') {
          setToasts(function (t) {
            var ref = {
              current: null
            };
            return [].concat(t, [{
              id: action.descriptor.id,
              placement: action.descriptor.placement,
              parentName: action.descriptor.name,
              parentRef: action.descriptor.ref,
              toastRef: ref,
              cachedSize: [0, 0],
              changedSizeRecently: false,
              attachRef: function attachRef(el) {
                ref.current = el;
              },
              data: action.descriptor.data,
              createdAt: Date.now()
            }]);
          });
        } else if (action.type === 'update') {
          setToasts(function (tsts) {
            return tsts.map(function (t) {
              if (t.id === action.id) {
                return Object.assign(Object.assign({}, t), {
                  data: Object.assign(Object.assign({}, t.data), action.newData)
                });
              }

              return t;
            });
          });
        } else if (action.type === 'remove') {
          setToasts(function (t) {
            return t.filter(function (toast) {
              return toast.id !== action.id;
            });
          });
        } else if (action.type === 'removeAll') {
          setToasts([]);
        } else if (action.type === 'removeAllByName') {
          setToasts(function (t) {
            return t.filter(function (toast) {
              return toast.parentName !== action.name;
            });
          });
        }
      });
      if (q.length) setQ([]);
    }, [q]);
    React.useLayoutEffect(function () {
      var newSizes = {};
      toasts.forEach(function (t) {
        if (!t.toastRef.current) return;

        var _t$toastRef$current$g = t.toastRef.current.getBoundingClientRect(),
            width = _t$toastRef$current$g.width,
            height = _t$toastRef$current$g.height;

        if (t.cachedSize[0] !== width || t.cachedSize[1] !== height) {
          newSizes[t.id] = [width, height];
        }
      });

      if (Object.keys(newSizes).length) {
        setToasts(function (tsts) {
          return tsts.map(function (t) {
            if (newSizes[t.id]) {
              return Object.assign(Object.assign({}, t), {
                cachedSize: newSizes[t.id],
                changedSizeRecently: true
              });
            }

            return t;
          });
        });
      }
    });
    return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TransitionGroup, {
      component: null
    }, toasts.map(renderToast))), portalInto || document.body);
  };
};

var createProvider = function createProvider(Context, ToastComponent) {
  var Viewport = createViewport(Context);
  return function (_ref) {
    var children = _ref.children,
        _ref$Component = _ref.Component,
        Component = _ref$Component === void 0 ? ToastComponent : _ref$Component,
        _ref$animationDuratio = _ref.animationDuration,
        animationDuration = _ref$animationDuratio === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$animationDuratio,
        _ref$defaultPlacement = _ref.defaultPlacement,
        defaultPlacement = _ref$defaultPlacement === void 0 ? DEFAULT_PLACEMENT : _ref$defaultPlacement,
        _ref$portalInto = _ref.portalInto,
        portalInto = _ref$portalInto === void 0 ? undefined : _ref$portalInto;

    var _React$useState = React.useState({}),
        refs = _React$useState[0],
        setRefs = _React$useState[1];

    var _React$useState2 = React.useState([]),
        q = _React$useState2[0],
        setQ = _React$useState2[1];

    var dispatchAction = function dispatchAction(action) {
      setQ(function (prevQ) {
        return [].concat(prevQ, [action]);
      });
    };

    var registerRef = function registerRef(name, ref) {
      setRefs(function (prevRefs) {
        var _Object$assign;

        return Object.assign(Object.assign({}, prevRefs), (_Object$assign = {}, _Object$assign[name] = ref, _Object$assign));
      });
    };

    var removeRef = function removeRef(name) {
      setRefs(function (prevRefs) {
        var _a = prevRefs,
            _b = name;
            _a[_b];
            var rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);

        return rest;
      });
      dispatchAction({
        type: 'removeAllByName',
        name: name
      });
    };

    var addToast = function addToast(name, data, placement) {
      if (placement === void 0) {
        placement = defaultPlacement;
      }

      var ref = refs[name];

      if (!ref || !ref.current) {
        console.warn("Tried to show toast on not mounted component '" + name + "'");
        return '';
      }

      var id = v4();
      dispatchAction({
        type: 'create',
        descriptor: {
          name: name,
          id: id,
          placement: placement,
          ref: ref,
          data: data
        }
      });
      return id;
    };

    var updateToast = function updateToast(id, newData) {
      dispatchAction({
        type: 'update',
        id: id,
        newData: newData
      });
    };

    var removeToast = function removeToast(id) {
      dispatchAction({
        type: 'remove',
        id: id
      });
    };

    var removeAllToastsByName = function removeAllToastsByName(name) {
      dispatchAction({
        type: 'removeAllByName',
        name: name
      });
    };

    var removeAllToasts = function removeAllToasts() {
      dispatchAction({
        type: 'removeAll'
      });
    };

    var _React$useState3 = React.useState(false),
        mounted = _React$useState3[0],
        setMounted = _React$useState3[1];

    React.useEffect(function () {
      setMounted(true);
    }, []);
    return /*#__PURE__*/React.createElement(Context.Provider, {
      value: {
        Component: Component,
        placement: defaultPlacement,
        animationDuration: animationDuration,
        q: q,
        setQ: setQ,
        refs: refs,
        registerRef: registerRef,
        removeRef: removeRef,
        addToast: addToast,
        updateToast: updateToast,
        removeToast: removeToast,
        removeAllToastsByName: removeAllToastsByName,
        removeAllToasts: removeAllToasts
      }
    }, children, mounted && /*#__PURE__*/React.createElement(Viewport, {
      portalInto: portalInto
    }));
  };
};

var createTarget = function createTarget(Context) {
  return /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
    var name = _ref.name,
        children = _ref.children;
    var ctx = React.useContext(Context);
    var ref = React.useRef(null);
    var composeRefs = useComposedRefs(forwardedRef, ref);
    React.useEffect(function () {
      ctx.registerRef(name, ref);
      return function () {
        ctx.removeRef(name);
      };
    }, []);
    return /*#__PURE__*/React.createElement(Slot, {
      ref: composeRefs
    }, children);
  });
};

var createCustomLocalToast = function createCustomLocalToast(component) {
  var Context = createContext(component);
  var Provider = createProvider(Context, component);
  var Target = createTarget(Context);

  var useCustomLocalToast = function useCustomLocalToast() {
    var _React$useContext = React.useContext(Context),
        addToast = _React$useContext.addToast,
        updateToast = _React$useContext.updateToast,
        removeToast = _React$useContext.removeToast,
        removeAllToastsByName = _React$useContext.removeAllToastsByName,
        removeAllToasts = _React$useContext.removeAllToasts;

    return {
      addToast: addToast,
      updateToast: updateToast,
      removeToast: removeToast,
      removeAllToastsByName: removeAllToastsByName,
      removeAllToasts: removeAllToasts
    };
  };

  return {
    Provider: Provider,
    Target: Target,
    useCustomLocalToast: useCustomLocalToast
  };
};

var createHocFromContext = function createHocFromContext(context) {
  return function (WrappedComponent) {
    var displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";

    var ComponentWithToast = function ComponentWithToast(props) {
      var contextProps = useContext(context);
      return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({}, contextProps, props));
    };

    ComponentWithToast.displayName = "withLocalToast(" + displayName + ")";
    return ComponentWithToast;
  };
};
var createHocFromHook = function createHocFromHook(hook) {
  return function (WrappedComponent) {
    var displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";

    var ComponentWithToast = function ComponentWithToast(props) {
      var hookProps = hook();
      return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({}, hookProps, props));
    };

    ComponentWithToast.displayName = "withLocalToast(" + displayName + ")";
    return ComponentWithToast;
  };
};

var IoIosCheckmarkCircle = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement("svg", Object.assign({
    stroke: "currentColor",
    fill: "currentColor",
    strokeWidth: "0",
    viewBox: "0 0 512 512",
    height: "1em",
    width: "1em",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M256 48C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48zm106.5 150.5L228.8 332.8h-.1c-1.7 1.7-6.3 5.5-11.6 5.5-3.8 0-8.1-2.1-11.7-5.7l-56-56c-1.6-1.6-1.6-4.1 0-5.7l17.8-17.8c.8-.8 1.8-1.2 2.8-1.2 1 0 2 .4 2.8 1.2l44.4 44.4 122-122.9c.8-.8 1.8-1.2 2.8-1.2 1.1 0 2.1.4 2.8 1.2l17.5 18.1c1.8 1.7 1.8 4.2.2 5.8z"
  }));
});
var IoIosInformationCircle = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement("svg", Object.assign({
    stroke: "currentColor",
    fill: "currentColor",
    strokeWidth: "0",
    viewBox: "0 0 512 512",
    height: "1em",
    width: "1em",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M256 48C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48zm19 304h-38.2V207.9H275V352zm-19.1-159.8c-11.3 0-20.5-8.6-20.5-20s9.3-19.9 20.5-19.9c11.4 0 20.7 8.5 20.7 19.9s-9.3 20-20.7 20z"
  }));
});
var IoCloseCircleSharp = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement("svg", Object.assign({
    stroke: "currentColor",
    fill: "currentColor",
    strokeWidth: "0",
    viewBox: "0 0 512 512",
    height: "1em",
    width: "1em",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48zm86.63 272L320 342.63l-64-64-64 64L169.37 320l64-64-64-64L192 169.37l64 64 64-64L342.63 192l-64 64z"
  }));
});
var RiErrorWarningFill = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement("svg", Object.assign({
    stroke: "currentColor",
    fill: "currentColor",
    strokeWidth: "0",
    viewBox: "0 0 24 24",
    height: "1em",
    width: "1em",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M0 0h24v24H0z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z"
  })));
});
var AiOutlineLoading = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement("svg", Object.assign({
    stroke: "currentColor",
    fill: "currentColor",
    strokeWidth: "0",
    viewBox: "0 0 1024 1024",
    height: "1em",
    width: "1em",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 0 0-94.3-139.9 437.71 437.71 0 0 0-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"
  }));
});

var animations = {
  top: 'rltTop',
  bottom: 'rltBottom',
  left: 'rltLeft',
  right: 'rltRight'
};
var iconByType = {
  info: IoIosInformationCircle,
  success: IoIosCheckmarkCircle,
  warning: RiErrorWarningFill,
  error: IoCloseCircleSharp,
  loading: AiOutlineLoading
};
var ToastComponent = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _clsx;

  var _React$useState = React.useState(props.animation.disableTransitions),
      disableTransitions = _React$useState[0],
      setDisableTransitions = _React$useState[1];

  React.useEffect(function () {
    if (props.animation.disableTransitions !== disableTransitions) {
      setDisableTransitions(props.animation.disableTransitions);
    }
  });
  var contentIsText = ['string', 'number'].includes(typeof props.data.text);
  var Icon = iconByType[props.data.type];
  var animation = 'none';
  if (props.animation.state === 'entering') animation = animations[props.placement] + " " + props.animation.duration + "ms linear 0s 1 normal";
  if (props.animation.state === 'exiting') animation = animations[props.placement] + " " + props.animation.duration + "ms linear 0s 1 reverse";
  var styles = Object.assign({
    transition: disableTransitions ? 'none' : '0.1s linear',
    animation: animation
  }, props.style);
  return /*#__PURE__*/React.createElement("div", {
    className: clsx((_clsx = {
      'react-local-toast': true
    }, _clsx["react-local-toast-" + props.data.type] = true, _clsx['react-local-toast-persistent'] = props.animation.duration === 0, _clsx["react-local-toast-" + props.animation.state] = true, _clsx["react-local-toast-" + props.placement] = true, _clsx['react-local-toast-disable-transitions'] = props.animation.disableTransitions, _clsx)),
    style: styles,
    ref: ref,
    role: "presentation",
    tabIndex: contentIsText ? -1 : 0
  }, /*#__PURE__*/React.createElement(Icon, {
    className: "react-local-toast-icon",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    role: ['warning', 'error'].includes(props.data.type) ? 'alert' : 'status',
    "aria-atomic": "true",
    "aria-live": 'assertive',
    className: "react-local-toast-text"
  }, props.data.text));
});

var _createCustomLocalToa = createCustomLocalToast(ToastComponent),
    Provider = _createCustomLocalToa.Provider,
    Target = _createCustomLocalToa.Target,
    useCustomLocalToast = _createCustomLocalToa.useCustomLocalToast;
var useLocalToast = function useLocalToast() {
  var _useCustomLocalToast = useCustomLocalToast(),
      addToast = _useCustomLocalToast.addToast,
      updateToast = _useCustomLocalToast.updateToast,
      removeToast = _useCustomLocalToast.removeToast,
      removeAllToastsByName = _useCustomLocalToast.removeAllToastsByName,
      removeAllToasts = _useCustomLocalToast.removeAllToasts;

  var showToast = function showToast(name, text, options) {
    var _ref = options || {},
        _ref$type = _ref.type,
        type = _ref$type === void 0 ? 'success' : _ref$type,
        _ref$placement = _ref.placement,
        placement = _ref$placement === void 0 ? DEFAULT_PLACEMENT : _ref$placement,
        _ref$duration = _ref.duration,
        duration = _ref$duration === void 0 ? 2500 : _ref$duration;

    var id = addToast(name, {
      type: type,
      text: text
    }, placement);

    if (duration > 0) {
      setTimeout(function () {
        return removeToast(id);
      }, duration);
    }

    return id;
  };

  return {
    showToast: showToast,
    updateToast: updateToast,
    removeToast: removeToast,
    removeAllToastsByName: removeAllToastsByName,
    removeAllToasts: removeAllToasts
  };
};
var withLocalToast = createHocFromHook(useLocalToast);

export { Provider as LocalToastProvider, Target as LocalToastTarget, createCustomLocalToast, createHocFromContext, createHocFromHook, useLocalToast, withLocalToast };
//# sourceMappingURL=react-local-toast.esm.js.map
